import {
  CONTENT_PAGE_KINDS,
  contentPageKindSchema,
  type ContentPage,
  type ContentPageKind,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  ConfirmDialog,
  Loading,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
  returnToPrevious,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import { contentPageQuery, keys, tenantApi } from '../api/tenant';
import { FormStatus, mergeErrors, NumberField } from '../components/fields';
import { StaleNotice, useVersionedDraft } from '../components/versioned';
import { he } from '../i18n/he';
import {
  contentPageCreateRequest,
  contentPageDraft,
  contentPageUpdateRequest,
} from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';

export function ContentPageNewPage() {
  const { kind } = useSearch({ from: '/app/pages/new' });
  return <ContentPageEditor page={undefined} kind={kind} />;
}

export function ContentPageEditPage() {
  const { pageId } = useParams({ from: '/app/pages/$pageId' });
  const page = useQuery(contentPageQuery(pageId));
  if (page.isPending) return <Loading />;
  if (page.isError) return <Alert>{errorMessage(page.error)}</Alert>;
  return <ContentPageEditor key={page.data.id} page={page.data} kind={page.data.kind} />;
}

/** Title, address, plain-text body, published, position; delete with a confirmation. */
function ContentPageEditor({
  page,
  kind,
}: {
  page: ContentPage | undefined;
  kind: ContentPageKind;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canWrite = useCan('store.settings.write');
  // A new page has no version yet (0); an existing one keeps the version its draft came from.
  const editor = useVersionedDraft(
    page ?? null,
    (value) => value?.version ?? 0,
    (value) => contentPageDraft(value ?? undefined, kind),
  );
  const { draft, set } = editor;
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useMutation({
    mutationFn: (run: () => Promise<ContentPage>) => run(),
    onSuccess: async (saved) => {
      editor.reset(saved);
      queryClient.setQueryData(keys.page(saved.id), saved);
      await queryClient.invalidateQueries({ queryKey: keys.pages() });
      if (page === undefined) {
        await navigate({ to: '/pages/$pageId', params: { pageId: saved.id } });
      }
    },
  });
  const remove = useMutation({
    mutationFn: () => (page === undefined ? Promise.resolve() : tenantApi.deletePage(page.id)),
    onSuccess: async () => {
      setConfirmDelete(false);
      await queryClient.invalidateQueries({ queryKey: keys.pages() });
      await navigate({ to: '/pages' });
    },
  });

  const reload = () => {
    if (page === undefined) return;
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...contentPageQuery(page.id), staleTime: 0 })
      // A failed read shows on the page itself (the page query's error).
      .then(editor.reset, () => undefined);
  };
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (page === undefined) {
      const parsed = contentPageCreateRequest(draft);
      setLocalErrors(parsed.ok ? NO_ERRORS : parsed.errors);
      if (parsed.ok) save.mutate(() => tenantApi.createPage(parsed.value));
      return;
    }
    const parsed = contentPageUpdateRequest(draft, editor.baseVersion);
    setLocalErrors(parsed.ok ? NO_ERRORS : parsed.errors);
    if (parsed.ok) save.mutate(() => tenantApi.updatePage(page.id, parsed.value));
  };
  const errors = mergeErrors(localErrors, save.error);

  return (
    <>
      <Link
        to="/pages"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.pages.back}
      </Link>
      <PageHeader
        title={page === undefined ? he.pages.newTitle : page.title}
        actions={
          page !== undefined && canWrite ? (
            <Button
              tone="danger"
              onClick={() => {
                remove.reset();
                setConfirmDelete(true);
              }}
            >
              {he.common.delete}
            </Button>
          ) : null
        }
      />
      <Card>
        <form onSubmit={onSubmit} noValidate data-testid="content-page-form">
          <fieldset disabled={!canWrite} className="flex flex-col gap-4">
            {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}
            <StaleNotice stale={editor.stale} onReload={reload} />
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label={he.pages.kind}
                name="kind"
                value={draft.kind}
                onChange={(event) => {
                  set({ kind: contentPageKindSchema.parse(event.target.value) });
                }}
              >
                {CONTENT_PAGE_KINDS.map((option) => (
                  <option key={option} value={option}>
                    {he.pages.kinds[option]}
                  </option>
                ))}
              </SelectField>
              <TextField
                label={he.pages.titleField}
                name="title"
                required
                maxLength={120}
                value={draft.title}
                error={errors['title']}
                onChange={(event) => {
                  set({ title: event.target.value });
                }}
              />
              <TextField
                label={he.pages.slug}
                name="slug"
                dir="ltr"
                required
                maxLength={60}
                hint={he.pages.slugHint}
                value={draft.slug}
                error={errors['slug']}
                onChange={(event) => {
                  set({ slug: event.target.value.toLowerCase() });
                }}
              />
              <NumberField
                label={he.common.position}
                name="position"
                value={draft.position}
                error={errors['position']}
                onChange={(position) => {
                  set({ position });
                }}
              />
            </div>
            <TextAreaField
              label={he.pages.body}
              name="body"
              rows={16}
              maxLength={50_000}
              hint={he.pages.bodyHint}
              value={draft.body}
              error={errors['body']}
              onChange={(event) => {
                set({ body: event.target.value });
              }}
            />
            <CheckboxField
              label={he.pages.published}
              checked={draft.isPublished}
              onChange={(event) => {
                set({ isPublished: event.target.checked });
              }}
            />
            <FormStatus
              error={errors['slug'] === undefined ? save.error : null}
              localInvalid={Object.keys(localErrors).length > 0 || errors['slug'] !== undefined}
              saved={save.isSuccess}
              {...(page === undefined
                ? { overrides: { CONFLICT: he.pages.full } }
                : { onReload: reload })}
            />
            {canWrite ? (
              <div>
                <Button type="submit" busy={save.isPending}>
                  {page === undefined ? he.common.create : he.common.save}
                </Button>
              </div>
            ) : null}
          </fieldset>
        </form>
      </Card>
      <ConfirmDialog
        open={confirmDelete}
        title={he.pages.deleteTitle}
        confirmLabel={he.common.delete}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => {
          remove.mutate();
        }}
        onClose={() => {
          setConfirmDelete(false);
        }}
      >
        <p>{he.common.deleteBody}</p>
        {remove.isError ? (
          <div className="mt-3">
            <Alert>{errorMessage(remove.error)}</Alert>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
